import { useState, useEffect } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Plus, Edit, Trash2, Search, ChevronLeft, ChevronRight, Package, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Componente genérico para CRUD de catálogos simples
 * Props:
 * - catalogKey: clave del catálogo (ej: "ambientes", "criticidad")
 * - title: título a mostrar
 * - icon: componente de icono Lucide
 * - extraFields: campos adicionales (ej: ["last_firmware"])
 */
const CatalogCRUD = ({ 
  catalogKey, 
  title, 
  icon: Icon = Package,
  extraFields = [],
  nameLabel = "Nombre",
  canCreate = true,
  canUpdate = true,
  canDelete = true 
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', last_firmware: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Sorting state
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Sort items client-side
  const sortedItems = [...items].sort((a, b) => {
    const aVal = (a[sortField] || '').toString().toLowerCase();
    const bVal = (b[sortField] || '').toString().toLowerCase();
    if (sortOrder === 'asc') {
      return aVal.localeCompare(bVal);
    }
    return bVal.localeCompare(aVal);
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-3 w-3 text-primary" /> 
      : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });
      if (search) params.append('search', search);
      
      const response = await api.get(`/config/catalogos/${catalogKey}?${params}`);
      setItems(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        pages: response.data.pages
      }));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchItems();
  }, [catalogKey, pagination.page]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchItems();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({ name: '', description: '', last_firmware: '' });
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      last_firmware: item.last_firmware || ''
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error(`${nameLabel} es requerido`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
      };
      
      if (extraFields.includes('last_firmware')) {
        payload.last_firmware = formData.last_firmware || null;
      }

      if (editingItem) {
        await api.put(`/config/catalogos/${catalogKey}/${editingItem.id}`, payload);
        toast.success('Registro actualizado exitosamente');
      } else {
        await api.post(`/config/catalogos/${catalogKey}`, payload);
        toast.success('Registro creado exitosamente');
      }
      setModalOpen(false);
      fetchItems();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al guardar';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      await api.delete(`/config/catalogos/${catalogKey}/${item.id}`);
      toast.success('Registro eliminado exitosamente');
      setDeleteConfirm(null);
      fetchItems();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al eliminar';
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
          <div>
            <h3 className="font-semibold text-foreground">{title}</h3>
            <p className="text-xs text-muted-foreground">{pagination.total} registros</p>
          </div>
        </div>
        <Button
          size="sm"
          className={`btn btn-primary h-8 ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={canCreate ? handleCreate : undefined}
          disabled={!canCreate}
          data-testid={`add-${catalogKey}-btn`}
        >
          <Plus className="h-3.5 w-3.5" />
          Nuevo
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-9 bg-background"
        />
      </div>

      {/* Table */}
      <Card className="card-subtle overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table-subtle text-sm">
                  <thead>
                    <tr>
                      <th 
                        className="cursor-pointer hover:text-primary transition-colors select-none"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-1.5">
                          {nameLabel.toUpperCase()}
                          <SortIcon field="name" />
                        </div>
                      </th>
                      <th 
                        className="cursor-pointer hover:text-primary transition-colors select-none"
                        onClick={() => handleSort('description')}
                      >
                        <div className="flex items-center gap-1.5">
                          DESCRIPCION
                          <SortIcon field="description" />
                        </div>
                      </th>
                      {extraFields.includes('last_firmware') && (
                        <th 
                          className="cursor-pointer hover:text-primary transition-colors select-none"
                          onClick={() => handleSort('last_firmware')}
                        >
                          <div className="flex items-center gap-1.5">
                            FIRMWARE
                            <SortIcon field="last_firmware" />
                          </div>
                        </th>
                      )}
                      <th className="w-20 text-right">ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.length === 0 ? (
                      <tr>
                        <td colSpan={extraFields.length + 3}>
                          <div className="text-center py-8 text-muted-foreground">
                            No se encontraron registros
                          </div>
                        </td>
                      </tr>
                    ) : sortedItems.map((item) => (
                      <tr key={item.id} data-testid={`row-${catalogKey}-${item.id}`}>
                        <td className="font-medium text-primary">{item.name}</td>
                        <td className="text-muted-foreground max-w-[250px] truncate">
                          {item.description || '-'}
                        </td>
                        {extraFields.includes('last_firmware') && (
                          <td className="text-muted-foreground text-xs font-mono">
                            {item.last_firmware || '-'}
                          </td>
                        )}
                        <td className="w-20 text-right">
                          <div className="flex justify-end gap-0.5">
                            <button
                              onClick={() => canUpdate && handleEdit(item)}
                              className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50 cursor-not-allowed' : ''}`}
                              disabled={!canUpdate}
                              title={canUpdate ? "Editar" : "Sin permiso"}
                              data-testid={`edit-${catalogKey}-${item.id}`}
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => canDelete && setDeleteConfirm(item)}
                              className={`action-btn-delete ${!canDelete ? 'opacity-50 cursor-not-allowed' : ''}`}
                              disabled={!canDelete}
                              title={canDelete ? "Eliminar" : "Sin permiso"}
                              data-testid={`delete-${catalogKey}-${item.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between px-4 py-2 border-t border-border/30">
                  <p className="text-xs text-muted-foreground">
                    Pág {pagination.page} de {pagination.pages}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} 
                      disabled={pagination.page === 1}
                      className="h-7 w-7"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} 
                      disabled={pagination.page >= pagination.pages}
                      className="h-7 w-7"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="modal-content max-w-md">
          <DialogHeader className="modal-header">
            <DialogTitle className="flex items-center gap-2">
              <Icon className="h-5 w-5 text-primary" />
              {editingItem ? `Editar ${title}` : `Nuevo ${title}`}
            </DialogTitle>
          </DialogHeader>
          <div className="modal-body py-4 space-y-4">
            <div>
              <Label className="text-sm font-medium">{nameLabel} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="input-subtle mt-1.5"
                placeholder={`Ingrese ${nameLabel.toLowerCase()}`}
                data-testid={`input-${catalogKey}-name`}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Descripcion</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="input-subtle mt-1.5"
                placeholder="Descripcion (opcional)"
              />
            </div>
            {extraFields.includes('last_firmware') && (
              <div>
                <Label className="text-sm font-medium">Ultimo Firmware</Label>
                <Input
                  value={formData.last_firmware}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_firmware: e.target.value }))}
                  className="input-subtle mt-1.5"
                  placeholder="Version de firmware"
                />
              </div>
            )}
          </div>
          <DialogFooter className="modal-footer">
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="modal-content">
          <DialogHeader className="modal-header">
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Confirmar Eliminacion
            </DialogTitle>
          </DialogHeader>
          <div className="modal-body py-4">
            <p className="text-muted-foreground">
              Desea eliminar <span className="font-semibold text-foreground">{deleteConfirm?.name}</span>?
            </p>
          </div>
          <DialogFooter className="modal-footer">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
            <Button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CatalogCRUD;
